import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  canActivateAgent,
  canArchiveTeam,
  canAssignAgentTeamsGroups,
  canBanCustomer,
  canBulkAssignTeamGroup,
  canActivateGroup,
  canArchiveGroup,
  canAssignGroupAgents,
  canAssignGroupChannels,
  canAssignGroupTeams,
  canAssignGroupWidgets,
  canBulkManageAgents,
  canBulkManageGroups,
  canChangeAgentStatus,
  canChangeCustomerStatus,
  canCreateCustomer,
  canCreateGroup,
  canCreateTeam,
  canDeleteAgent,
  canDeleteCustomer,
  canDeleteTeam,
  canEditAgent,
  canEditCustomer,
  canEditGroup,
  canEditTeam,
  canInviteAgent,
  canManageTeamMembers,
  canSuspendAgent,
  canUnbanCustomer,
  canViewGroups,
  getCurrentUserFromState,
  isCustomerBanAdminRole,
} from "../utils/peoplePermissions";

export default function usePeoplePermissions() {
  const loginState = useSelector((state) => state.loginToken);
  const coUserState = useSelector((state) => state.loginTokenCoUser);

  return useMemo(() => {
    const currentUser = getCurrentUserFromState({ loginToken: loginState, loginTokenCoUser: coUserState });
    const roleName = currentUser?.roleName || "";
    const userId = currentUser?.id || null;

    const forAgent = (targetAgentId, targetRoleName) => ({
      canEdit: canEditAgent(roleName, userId, targetAgentId, targetRoleName),
      canChangeStatus: canChangeAgentStatus(roleName, userId, targetAgentId, targetRoleName),
      canSuspend: canSuspendAgent(roleName, targetRoleName),
      canActivate: canActivateAgent(roleName, targetRoleName),
      canDelete: canDeleteAgent(roleName, targetRoleName),
    });

    return {
      roleName,
      userId,
      customers: {
        canCreate: canCreateCustomer(roleName),
        canEdit: canEditCustomer(roleName),
        canChangeStatus: canChangeCustomerStatus(roleName),
        canDelete: canDeleteCustomer(roleName),
        canBan: canBanCustomer(roleName),
        canUnban: canUnbanCustomer(roleName),
        isBanAdmin: isCustomerBanAdminRole(roleName),
      },
      teams: {
        canCreate: canCreateTeam(roleName),
        canEdit: canEditTeam(roleName),
        canManageMembers: canManageTeamMembers(roleName),
        canArchive: canArchiveTeam(roleName),
        canDelete: canDeleteTeam(roleName),
        canBulkAssign: canBulkAssignTeamGroup(roleName),
      },
      agents: {
        canInvite: canInviteAgent(roleName),
        canBulkManage: canBulkManageAgents(roleName),
        canAssignTeamsGroups: canAssignAgentTeamsGroups(roleName),
        forAgent,
      },
      groups: {
        canView: canViewGroups(),
        canCreate: canCreateGroup(roleName),
        canEdit: canEditGroup(roleName),
        canAssignAgents: canAssignGroupAgents(roleName),
        canAssignChannels: canAssignGroupChannels(roleName),
        canAssignTeams: canAssignGroupTeams(roleName),
        canAssignWidgets: canAssignGroupWidgets(roleName),
        canArchive: canArchiveGroup(roleName),
        canActivate: canActivateGroup(roleName),
        canBulkManage: canBulkManageGroups(roleName),
      },
    };
  }, [loginState, coUserState]);
}
